/**
 * useMasterGanttFilters.ts
 * Phase 4, Item 5 — URL-sticky filters for the Master Gantt view.
 *
 * Sister hook to useMachineScheduleFilters (same pattern, different URL params
 * and an additional `showProposals` boolean).
 *
 * URL param schema:
 *   labSection    — comma-separated LabSection values   e.g. "Rock,Triaxial"
 *   machine       — comma-separated machine codes        e.g. "M-01,M-02"
 *   workType      — comma-separated MachineBookingSource e.g. "Quote,Manual"
 *   proposals     — "on" (default, show tentative bars) | "off" (hide them)
 *   quoteKey      — a single QuoteKey (BUG-2026-0079); when set, only bookings
 *                   for that quote remain visible on the Gantt
 *   workOrderKey  — a single WorkOrderKey (BUG-2026-0079); when set, only
 *                   bookings for that work order remain visible
 *
 * Multi-select toggle is session-only state (not URL-sticky) — it is a
 * transient UI mode that should not survive navigation.
 */

import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { LabSection, MachineBookingSource } from '../../../types/schedule';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MasterGanttFilterState {
  /** Selected lab sections (empty = all). */
  labSections: LabSection[];
  /** Selected machine codes (empty = all). */
  machineCodes: string[];
  /** Selected booking source types (empty = all). */
  workTypes: MachineBookingSource[];
  /**
   * Whether to show proposal (Tentative) bookings on the Gantt.
   * Default true. Maps to URL param `proposals=on|off`.
   */
  showProposals: boolean;
  /**
   * BUG-2026-0079: When set (non-null), only bookings whose linked
   * ScheduleTask has QuoteKey === quoteKey are shown.
   * Selectable via the Quote dropdown in the filter row.
   * Null means "no quote filter active" (default).
   */
  quoteKey: string | null;
  /**
   * BUG-2026-0079: When set (non-null), only bookings whose linked
   * ScheduleTask has WorkOrderKey === workOrderKey are shown.
   * Selectable via the Work Order dropdown in the filter row.
   * Null means "no work order filter active" (default).
   */
  workOrderKey: string | null;
}

export type MasterGanttFilterSetter = (
  patch: Partial<MasterGanttFilterState>,
) => void;

// ---------------------------------------------------------------------------
// URL param keys
// ---------------------------------------------------------------------------

const PARAM_LAB_SECTION    = 'labSection';
const PARAM_MACHINE        = 'machine';
const PARAM_WORK_TYPE      = 'workType';
const PARAM_PROPOSALS      = 'proposals';
/** BUG-2026-0079: filter Master Gantt to a single quote. */
const PARAM_QUOTE_KEY      = 'quoteKey';
/** BUG-2026-0079: filter Master Gantt to a single work order. */
const PARAM_WORK_ORDER_KEY = 'workOrderKey';

// ---------------------------------------------------------------------------
// Pure parse/serialize helpers (exported for test use)
// ---------------------------------------------------------------------------

/** Parse a comma-separated param value into an array, stripping empties. */
export function parseMasterGanttCommaSeparated(value: string | null): string[] {
  if (!value) return [];
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Serialize an array to a comma-separated string, or null if empty. */
export function serializeMasterGanttCommaSeparated(values: string[]): string | null {
  if (values.length === 0) return null;
  return values.join(',');
}

/** Parse all filter params from a plain Record<string, string | null>. */
export function parseMasterGanttFilterParams(
  params: Record<string, string | null>,
): MasterGanttFilterState {
  const labSections = parseMasterGanttCommaSeparated(params[PARAM_LAB_SECTION]) as LabSection[];
  const machineCodes = parseMasterGanttCommaSeparated(params[PARAM_MACHINE]);
  const workTypes = parseMasterGanttCommaSeparated(params[PARAM_WORK_TYPE]) as MachineBookingSource[];
  // proposals defaults to "on" (show) when absent; "off" = hide
  const proposalsRaw = params[PARAM_PROPOSALS];
  const showProposals = proposalsRaw !== 'off';
  // BUG-2026-0079: quote / work-order keys default to null (no filter).
  const quoteKey = params[PARAM_QUOTE_KEY] ?? null;
  const workOrderKey = params[PARAM_WORK_ORDER_KEY] ?? null;

  return { labSections, machineCodes, workTypes, showProposals, quoteKey, workOrderKey };
}

/** Serialize filter state to a Record of param key → value (null = delete param). */
export function serializeMasterGanttFilterParams(
  state: MasterGanttFilterState,
): Record<string, string | null> {
  return {
    [PARAM_LAB_SECTION]: serializeMasterGanttCommaSeparated(state.labSections),
    [PARAM_MACHINE]:     serializeMasterGanttCommaSeparated(state.machineCodes),
    [PARAM_WORK_TYPE]:   serializeMasterGanttCommaSeparated(state.workTypes),
    // Omit proposals param when it is the default (true/on) to keep URLs clean.
    [PARAM_PROPOSALS]: state.showProposals ? null : 'off',
    // BUG-2026-0079: quote / WO keys — null removes the param; UUID preserves it.
    [PARAM_QUOTE_KEY]:      state.quoteKey ?? null,
    [PARAM_WORK_ORDER_KEY]: state.workOrderKey ?? null,
  };
}

/** Return the empty / default filter state. */
export function defaultMasterGanttFilterState(): MasterGanttFilterState {
  return {
    labSections: [],
    machineCodes: [],
    workTypes: [],
    showProposals: true,
    quoteKey: null,
    workOrderKey: null,
  };
}

/** Whether the filter state is the default (no active filters, proposals ON). */
export function isMasterGanttFilterDefault(state: MasterGanttFilterState): boolean {
  return (
    state.labSections.length === 0 &&
    state.machineCodes.length === 0 &&
    state.workTypes.length === 0 &&
    state.showProposals === true &&
    state.quoteKey === null &&
    state.workOrderKey === null
  );
}

// ---------------------------------------------------------------------------
// Client-side filter predicate for Master Gantt bookings
// ---------------------------------------------------------------------------

export interface MasterGanttBookingForFilter {
  status: string;      // 'Tentative' | 'Committed'
  source: string;      // 'Quote' | 'WorkOrder' | 'Manual'
  machineCode: string;
  /**
   * Machine.LabSection as the API returns it (`string | null`, matching
   * MasterGanttMachineBooking.machineLabSection — BUG-2026-0207 widened this
   * from `LabSection | null` so the real response rows satisfy the predicate).
   * null / unknown values are treated as 'Other'.
   */
  machineLabSection: string | null;
}

/**
 * Apply master-gantt filter state to a list of bookings.
 * Used client-side to hide rows before Highcharts renders.
 *
 * Note: quoteKey / workOrderKey filters are NOT applied here — they require
 * project/work-order context (machineBookings are nested under the WO that
 * owns them, so the WO key is known at the row-builder level, not on the
 * individual booking). The MasterGanttPage applies those filters when
 * walking the hierarchy in `filteredGanttData`.
 */
export function applyMasterGanttFilters<T extends MasterGanttBookingForFilter>(
  bookings: T[],
  state: MasterGanttFilterState,
): T[] {
  return bookings.filter((b) => {
    // Proposals (tentative) filter
    if (!state.showProposals && b.status === 'Tentative') return false;

    // Lab section filter (if machine has no section, treat as 'Other')
    if (state.labSections.length > 0) {
      const section = b.machineLabSection ?? 'Other';
      if (!state.labSections.includes(section as LabSection)) return false;
    }

    // Machine code filter
    if (state.machineCodes.length > 0) {
      if (!state.machineCodes.includes(b.machineCode)) return false;
    }

    // Work type / Source filter
    if (state.workTypes.length > 0) {
      if (!state.workTypes.includes(b.source as MachineBookingSource)) return false;
    }

    return true;
  });
}

// ---------------------------------------------------------------------------
// React hook
// ---------------------------------------------------------------------------

export interface UseMasterGanttFiltersReturn {
  filterState: MasterGanttFilterState;
  setFilter: MasterGanttFilterSetter;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  /** Session-only multi-select toggle. Not URL-sticky. */
  multiSelectOn: boolean;
  setMultiSelectOn: (on: boolean) => void;
  /** Set of bar IDs currently selected (when multiSelectOn). */
  selectedKeys: Set<string>;
  addSelection: (key: string) => void;
  removeSelection: (key: string) => void;
  clearSelection: () => void;
}

/**
 * Hook that reads filter state from the URL and writes changes back.
 * Multi-select is held in local React state (not URL).
 */
export function useMasterGanttFilters(): UseMasterGanttFiltersReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  // ── URL-backed filter state ───────────────────────────────────────────────
  const filterState = useMemo<MasterGanttFilterState>(() => {
    const raw: Record<string, string | null> = {
      [PARAM_LAB_SECTION]:    searchParams.get(PARAM_LAB_SECTION),
      [PARAM_MACHINE]:        searchParams.get(PARAM_MACHINE),
      [PARAM_WORK_TYPE]:      searchParams.get(PARAM_WORK_TYPE),
      [PARAM_PROPOSALS]:      searchParams.get(PARAM_PROPOSALS),
      [PARAM_QUOTE_KEY]:      searchParams.get(PARAM_QUOTE_KEY),
      [PARAM_WORK_ORDER_KEY]: searchParams.get(PARAM_WORK_ORDER_KEY),
    };
    return parseMasterGanttFilterParams(raw);
  }, [searchParams]);

  const hasActiveFilters = !isMasterGanttFilterDefault(filterState);

  const setFilter = useCallback<MasterGanttFilterSetter>(
    (patch) => {
      const next: MasterGanttFilterState = { ...filterState, ...patch };
      const serialized = serializeMasterGanttFilterParams(next);
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(serialized)) {
            if (value === null) {
              updated.delete(key);
            } else {
              updated.set(key, value);
            }
          }
          return updated;
        },
        { replace: true },
      );
    },
    [filterState, setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const updated = new URLSearchParams(prev);
        updated.delete(PARAM_LAB_SECTION);
        updated.delete(PARAM_MACHINE);
        updated.delete(PARAM_WORK_TYPE);
        updated.delete(PARAM_PROPOSALS);
        updated.delete(PARAM_QUOTE_KEY);
        updated.delete(PARAM_WORK_ORDER_KEY);
        return updated;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  // ── Session-only multi-select state ──────────────────────────────────────
  const [multiSelectOn, setMultiSelectOnRaw] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  const setMultiSelectOn = useCallback((on: boolean) => {
    setMultiSelectOnRaw(on);
    if (!on) {
      // Clear selection when turning off multi-select
      setSelectedKeys(new Set());
    }
  }, []);

  const addSelection = useCallback((key: string) => {
    setSelectedKeys((prev) => new Set([...prev, key]));
  }, []);

  const removeSelection = useCallback((key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedKeys(new Set());
  }, []);

  return {
    filterState,
    setFilter,
    clearFilters,
    hasActiveFilters,
    multiSelectOn,
    setMultiSelectOn,
    selectedKeys,
    addSelection,
    removeSelection,
    clearSelection,
  };
}
